/**
 * Responsibility: describe the terraced vineyard biome — a hillside quantised into steps, planted along its
 * contours, with the occasional winery.
 *
 * ## Shaped unlike the last three biomes, on purpose
 *
 * The city, the industrial estate and the fairground are block grids of structures on flat ground. This one
 * inverts every part of that: its identity is the **terrain**, it *needs* a slope rather than refusing one,
 * and what it plants is a field of ground lines rather than objects. Its relatives are farmland and mesa, and
 * it reuses an idea from each — mesa's elevation quantisation, and farmland's contour-following rows.
 *
 * ## The one idea everything else follows from
 *
 * **Rows and retaining walls are the level curves of this biome's own elevation field.** `contourLines.js`
 * traces them. That single decision pays for itself three times over: the rows wrap around a spur and pinch
 * into a gully with no path-following code, they cross tile seams without either tile knowing the other
 * exists, and their plan spacing tightens on steeper ground exactly as real terracing does.
 *
 * It also puts the walls in the right place *by construction*. The terrain is quantised at `TERRACE_STEP`, and
 * a wall is drawn at every contour level that is an exact multiple of that step — which is the height where
 * the drawn surface finishes rising and a tread begins. Nothing positions a wall on a lip; a wall is what the
 * lip *is*.
 *
 * ## Why the field being contoured is not the ground being drawn
 *
 * The contour comes from this biome's own hillside; the geometry is lifted onto the blended surface that is
 * actually on screen. Contouring the blend instead would make the rows swing about wherever a neighbouring
 * biome happened to lift the ground, and at a border the pattern would stop being the vineyard's.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createFbm, toUnitRange } from '../noise.js';
import { createLocalRng, deriveSeed } from '../rng.js';
import { createContourLines } from '../contourLines.js';
import { PALETTE, VINEYARD, VINEYARD_LINE_PALETTE } from '../../config.js';

/** Linear interpolation, for turning a 0..1 roll into a range. */
function lerp(from, to, t) {
  return from + (to - from) * t;
}

/** Positive modulo, so a level index below zero lands on the same tread pattern as one above it. */
function mod(value, divisor) {
  return ((value % divisor) + divisor) % divisor;
}

/** Smooth 0..1 ramp between two thresholds. Zero below, one above, eased between. */
function smoothstep(from, to, value) {
  if (to <= from) return value >= to ? 1 : 0;
  const t = Math.min(1, Math.max(0, (value - from) / (to - from)));
  return t * t * (3 - 2 * t);
}

/**
 * The smooth hillside, before it is cut into terraces.
 *
 * Kept separate from the terraced version because **both are needed and for different jobs**: the terraces are
 * the ground, and this is what the contours are traced on. Tracing the terraced version would put every level
 * curve exactly on a riser, since that is the only place a quantised field varies — the rows would collapse
 * onto the walls and there would be no planting between them.
 */
function createHillside(seed) {
  const field = createFbm(seed, 'elevation:vineyard', {
    octaves: VINEYARD.OCTAVES,
    baseFrequency: 1 / VINEYARD.WAVELENGTH,
    persistence: VINEYARD.PERSISTENCE,
    lacunarity: VINEYARD.LACUNARITY,
  });

  return function sampleHillside(x, z) {
    return toUnitRange(field(x, z)) * VINEYARD.MAX_ELEVATION;
  };
}

/**
 * Cut a smooth height into terraces.
 *
 * Most of each step is flat tread and the last `RISER_FRACTION` of it is an eased rise, so a terrace has a
 * rounded lip rather than a stack of aliased corners.
 *
 * At a step boundary the two sides agree — approaching from below the ease reaches 1, and from above the next
 * level starts at 0 — so the surface is continuous and a wall drawn at that exact height sits on the lip.
 *
 * **Clamped to the input**, which keeps `maxElevation` exactly honest rather than nearly. The eased fraction is
 * below the raw one almost everywhere, but an S-curve overshoots it very slightly near the top of a riser, and
 * a bound that is true except in a fraction of a unit is the kind of thing that becomes a puzzle later.
 */
function terrace(height) {
  const step = VINEYARD.TERRACE_STEP;
  const level = Math.floor(height / step);
  const fraction = height / step - level;

  return Math.min(height, (level + smoothstep(1 - VINEYARD.RISER_FRACTION, 1, fraction)) * step);
}

/** Where each estate's buildings may stand. A field belongs to the tile containing its centre. */
function estateRange(start, length) {
  const size = VINEYARD.ESTATE_SIZE;

  return {
    first: Math.ceil(start / size - 0.5),
    last: Math.ceil((start + length) / size - 0.5) - 1,
  };
}

export const vineyard = {
  id: 'vineyard',
  label: 'Terraced vineyard',

  palette: PALETTE.vineyard,

  /**
   * Rows and walls get their own gradient, read by a stored tint rather than by height.
   *
   * The ground is deliberately muted so the worked texture carries the biome, which is farmland's reasoning.
   * The tint is what farmland could not do: a wall and the row beside it are a few units apart on a 360-unit
   * hillside, so height could never tell them apart.
   */
  structurePalette: VINEYARD_LINE_PALETTE,

  suitabilityBias: VINEYARD.SUITABILITY_BIAS,

  /**
   * Build the elevation function: a smooth hillside, cut into steps, with a gentle undulation on top.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number) => number}
   */
  createElevation(seed) {
    const hillside = createHillside(seed);

    /*
      A separate field for the undulation, so it is statistically independent of where the terraces fall.
      Sharing one would make the ground rise exactly where the steps do and the two would read as one feature —
      the same call mesa makes for its floor relief.
    */
    const relief = createFbm(seed, 'relief:vineyard', {
      octaves: 2,
      baseFrequency: 1 / VINEYARD.RELIEF_WAVELENGTH,
      persistence: 0.4,
      lacunarity: VINEYARD.LACUNARITY,
    });

    return function sampleVineyardElevation(x, z) {
      return terrace(hillside(x, z)) + toUnitRange(relief(x, z)) * VINEYARD.RELIEF;
    };
  },

  /** Highest elevation this biome can produce. Terracing never raises a height, so the hillside bounds it. */
  maxElevation: VINEYARD.MAX_ELEVATION + VINEYARD.RELIEF,

  /**
   * Build the structure placement function: contour rows and walls, plus the occasional winery.
   *
   * @param {number} seed - Base world seed.
   * @returns {(region: object, context: object) => Array<object>}
   */
  createStructures(seed) {
    const hillside = createHillside(seed);
    const estateSeed = deriveSeed(seed, 'structures:vineyard');

    /*
      Which parts of a hillside are planted. Without it the whole region is under vine, which is both far too
      much line and untrue — a real slope is planted in blocks separated by scrub, rock and tracks. The forest's
      stand field solves the same problem the same way.
    */
    const plantedField = createFbm(seed, 'planted:vineyard', {
      octaves: VINEYARD.PLANTED_OCTAVES,
      baseFrequency: 1 / VINEYARD.PLANTED_WAVELENGTH,
    });

    /*
      One contour level per row, with every `ROWS_PER_TERRACE`th level being a wall. Spacing the levels this way
      is what couples the planting to the terrain: the walls land on multiples of the terrace step, which is
      exactly where the ground finishes rising.
    */
    const levelSpacing = VINEYARD.TERRACE_STEP / Math.max(1, VINEYARD.ROWS_PER_TERRACE);
    const contours = createContourLines({ levelSpacing, grid: VINEYARD.GRID });

    /**
     * @param {{originX: number, originZ: number, size: number}} region
     * @param {object} context
     */
    return function collectStructures(region, { sampleWeight, sampleGround, sampleGroundDetail, isReserved }) {
      const structures = [];

      /**
       * This biome's weight, sampled **once for the whole tile**.
       *
       * A suitability field runs at `BIOME.FIELD_WAVELENGTH` — tens of thousands of units — so it varies by a
       * few percent across a 1,600-unit tile and cannot possibly change within a 50-unit cell. Asking per cell
       * cost about five hundred biome-field samples a tile, which on its own made a vineyard tile the most
       * expensive in the world. This is the same argument `TILES.PRESENCE_PROBES` makes for probing a tile's
       * presence on a coarse grid rather than everywhere.
       */
      const tileWeight = sampleWeight(region.originX + region.size / 2, region.originZ + region.size / 2);

      /*
        No weight, nothing here. An explicit gate rather than something the thinning happens to produce: the
        thinning raises a threshold, and a raised threshold still passes wherever the planted field is high
        enough — so at a weight of zero this biome would otherwise still plant its best ground in a region that
        belongs to somebody else.
      */
      if (tileWeight <= 0) return structures;

      /**
       * Whether a cell of hillside is planted.
       *
       * Four questions, **in cost order, cheapest first**, and every one is load-bearing. Order matters more
       * than usual here because this runs for roughly a thousand cells a tile: asking the expensive questions
       * first made a vineyard tile cost 2.25 times a city tile, and reordering was most of getting that back.
       *
       * - **The slope band**, from the cell slope the contour tracer hands in — free, since it comes from the
       *   corner values already sampled to find the crossings. It is also the *right* slope: this biome's own
       *   hillside rather than the blended surface, so a neighbour lifting the ground cannot change what the
       *   vineyard decides to plant. Below the floor there is nothing to terrace and the contour spacing runs
       *   away; above the ceiling nobody plants and the treads would be finer than the lattice can draw.
       * - **The planted field** — one unblended noise sample, so a slope is worked in blocks rather than wall
       *   to wall. The forest's stand field does the same job.
       * - **The cross-biome reservation**, which above all means not drawing rows across a lake. Last because
       *   it is the only one that can consult several other biomes.
       */
      const plantedAt = (x, z, slope) => {
        if (slope < VINEYARD.MIN_SLOPE || slope > VINEYARD.MAX_SLOPE) return false;

        /*
          Ramped rather than a hard threshold, so a block of planting has a soft edge rather than a contour
          line of its own.

          **Thinned by raising the threshold rather than by scaling the result**, and the difference is not
          cosmetic: the first version multiplied the planted field by the biome weight and tested the product
          against zero, which thins nothing at all — a positive field times a positive weight is still positive.
          It only stopped planting where the weight was exactly zero, so a region ended at a hard line, which is
          the failure the thinning-not-fading rule exists to prevent. Raising the bar as the weight falls means
          only the most suitable ground stays planted at a border.
        */
        const cutoff =
          VINEYARD.PLANTED_THRESHOLD + (1 - Math.min(1, tileWeight)) * VINEYARD.EDGE_TIGHTEN;

        const planted = smoothstep(cutoff, cutoff + VINEYARD.PLANTED_RAMP, toUnitRange(plantedField(x, z)));
        return planted > 0;
      };

      /**
       * Whether a single grid point is clear of every other biome's reservation — which above all means not
       * being in a lake.
       *
       * **Asked per grid point rather than per cell, and a cell needs all four corners.** A contour crossing can
       * sit anywhere on a cell's boundary, up to 35 units from its centre, and the water clearance probes a
       * centre plus a *ring* — so a shoreline passed between the two and rows were drawn across a lake, which
       * the nothing-stands-in-water test caught. Requiring the corners puts a full cell of margin around the
       * water instead.
       */
      const clearAt = (x, z) => !isReserved?.(x, z, VINEYARD.GRID / 2);

      /*
        One structure for the whole tile's planting rather than one per row. Hundreds of short segments as
        objects would cost more in allocation than the geometry costs to draw — the same call the water surface
        and the crop rows make.
      */
      const rows = contours(region, {
        valueAt: hillside,
        groundAt: sampleGround,
        allowAt: plantedAt,
        allowPointAt: clearAt,
        lift: VINEYARD.ROW_LIFT,

        /**
         * Which contour levels carry vines, and how bright they are.
         *
         * **The level that lands on a terrace boundary carries nothing.** The first version drew a bright
         * retaining wall there, and flown it was backwards — the lines were all anyone saw and the terraced
         * ground beneath read as a smooth hill with stripes on it. The step in the terrain is the feature now,
         * so the lip is left bare and the rows sit on the flat benches between.
         *
         * The remaining rows vary slightly in tint by their position up the tread, so three rows on one bench
         * are not a single flat colour.
         */
        tintForLevel: (level) => {
          const within = mod(level, VINEYARD.ROWS_PER_TERRACE);
          if (within === 0) return null;

          const across = within / VINEYARD.ROWS_PER_TERRACE;
          return VINEYARD.ROW_TINT + (across - 0.5) * VINEYARD.ROW_TINT_VARIATION;
        },
      });

      if (rows.count > 0) {
        structures.push({ kind: 'vineyardLines', data: rows.data, count: rows.count });
      }

      // --- the wineries ------------------------------------------------------

      const size = VINEYARD.ESTATE_SIZE;
      const xRange = estateRange(region.originX, region.size);
      const zRange = estateRange(region.originZ, region.size);

      for (let estateX = xRange.first; estateX <= xRange.last; estateX += 1) {
        for (let estateZ = zRange.first; estateZ <= zRange.last; estateZ += 1) {
          const minX = estateX * size;
          const minZ = estateZ * size;

          const weight = sampleWeight(minX + size / 2, minZ + size / 2);
          if (weight <= 0) continue;

          /*
            One stream per estate, keyed by estate index rather than by tile, so the layout does not depend on
            which tile is being built. Roll order is part of the world's identity — append only.
          */
          const random = createLocalRng(estateSeed, estateX, estateZ);
          const wineryRoll = random();
          const placeX = random();
          const placeZ = random();
          const facingRoll = random();

          if (wineryRoll >= VINEYARD.WINERY_CHANCE * weight) continue;

          const yardX = minX + lerp(size * 0.25, size * 0.75, placeX);
          const yardZ = minZ + lerp(size * 0.25, size * 0.75, placeZ);

          /*
            A winery stands on a tread, not across a step. The slope limit is tight for that reason: a building
            straddling a riser would have one wall buried and the other in the air, and the terraces are the one
            terrain in the world where that is likely rather than rare.
          */
          const spot = sampleGroundDetail(yardX, yardZ);
          if (spot.slope > VINEYARD.WINERY_MAX_SLOPE) continue;

          const facing = facingRoll * Math.PI * 2;
          const cos = Math.cos(facing);
          const sin = Math.sin(facing);

          const cellarX = yardX + cos * (VINEYARD.HOUSE_WIDTH + VINEYARD.CELLAR_WIDTH) * 0.6;
          const cellarZ = yardZ + sin * (VINEYARD.HOUSE_WIDTH + VINEYARD.CELLAR_WIDTH) * 0.6;

          const tankX = cellarX - sin * (VINEYARD.CELLAR_DEPTH * 0.5 + VINEYARD.TANK_RADIUS * 2);
          const tankZ = cellarZ + cos * (VINEYARD.CELLAR_DEPTH * 0.5 + VINEYARD.TANK_RADIUS * 2);

          /*
            All three tested, not just the yard. The cellar and the tank sit at offsets set by the yard's
            *facing*, so they can be tens of units away in an arbitrary direction — farmland learned this when a
            dry yard turned out to have its barn in a lake.
          */
          const clear =
            !isReserved?.(yardX, yardZ, VINEYARD.CELLAR_WIDTH) &&
            !isReserved?.(cellarX, cellarZ, VINEYARD.CELLAR_WIDTH) &&
            !isReserved?.(tankX, tankZ, VINEYARD.TANK_RADIUS * 2);

          if (!clear) continue;

          structures.push({
            kind: 'gableBuilding',
            centerX: yardX,
            centerZ: yardZ,
            width: VINEYARD.HOUSE_WIDTH,
            depth: VINEYARD.HOUSE_DEPTH,
            groundY: sampleGround(yardX, yardZ),
            wallHeight: VINEYARD.HOUSE_WALL_HEIGHT,
            roofHeight: VINEYARD.HOUSE_ROOF_HEIGHT,
            facing,
            weight,
          });

          structures.push({
            kind: 'gableBuilding',
            centerX: cellarX,
            centerZ: cellarZ,
            width: VINEYARD.CELLAR_WIDTH,
            depth: VINEYARD.CELLAR_DEPTH,
            groundY: sampleGround(cellarX, cellarZ),
            wallHeight: VINEYARD.CELLAR_WALL_HEIGHT,
            roofHeight: VINEYARD.CELLAR_ROOF_HEIGHT,
            facing,
            weight,
          });

          structures.push({
            kind: 'silo',
            x: tankX,
            z: tankZ,
            groundY: sampleGround(tankX, tankZ),
            radius: VINEYARD.TANK_RADIUS,
            height: VINEYARD.TANK_HEIGHT,
            sides: VINEYARD.TANK_SIDES,
            weight,
          });
        }
      }

      return structures;
    };
  },
};

/** Exported so tests can reason about the terracing without restating the arithmetic. */
export { terrace, createHillside };
